import { useCallback, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  PiArrowsClockwise,
  PiPencilSimple,
  PiPlus,
  PiTrash,
} from 'react-icons/pi'

import confirm from 'components/dialogConfirm'
import FbButton from 'components/FbUI/FbButton'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  type SenderEmail,
  useDeleteSenderEmail,
  useGetWorkspaceSenderEmails,
  useSyncWorkspaceSenderEmails,
} from 'features/campaigns/sender_emails_api'
import { useGetHeadlessUIToken } from 'features/campaigns/headless_ui_api'
import { SenderEmailsTable } from 'features/campaigns/SequenceTab/SenderEmailsTable'
import { useMountEffect } from 'hooks/useMountEffect'
import { EditSenderEmailModal } from './EditSenderEmailModal'

const DESCRIPTION_TEXT =
  'Manage your sender email accounts. Connect a new email or sync to keep your sender emails up to date.'

const HEADLESS_UI_BASE_URL = 'https://outbound.firstbite.io/headless-ui-login'

function getApiErrorDetail(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { detail?: string } } })
      .response?.data
    if (data?.detail) return data.detail
  }
  return fallback
}

export function SenderEmailSettings() {
  const [editingSenderEmail, setEditingSenderEmail] =
    useState<SenderEmail | null>(null)

  const {
    data: senderEmails,
    isLoading,
    isFetching,
  } = useGetWorkspaceSenderEmails()

  const syncMutation = useSyncWorkspaceSenderEmails()
  const deleteMutation = useDeleteSenderEmail()
  const headlessTokenMutation = useGetHeadlessUIToken()

  const popupCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )

  useMountEffect(() => () => {
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current)
      popupCheckIntervalRef.current = null
    }
  })

  const handleSync = useCallback(async () => {
    try {
      const result = await syncMutation.mutateAsync()
      toast.success(
        `Synced: ${result.created} added, ${result.updated} updated`
      )
    } catch (error: unknown) {
      toast.error(getApiErrorDetail(error, 'Failed to sync sender emails'))
    }
  }, [syncMutation])

  const handleConnectEmail = useCallback(async () => {
    try {
      const { token } = await headlessTokenMutation.mutateAsync()
      const addEmailUrl = `${HEADLESS_UI_BASE_URL}?token=${token}`

      const width = 600
      const height = 700
      const left = (screen.width - width) / 2
      const top = (screen.height - height) / 2
      const popup = window.open(
        addEmailUrl,
        'AddEmailConnection',
        `width=${width},height=${height},left=${left},top=${top}`
      )

      if (!popup) {
        toast.error(
          'Popup was blocked. Please allow popups for this site and try again.'
        )
        return
      }

      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current)
      }
      const intervalId = setInterval(() => {
        if (popup.closed) {
          clearInterval(intervalId)
          popupCheckIntervalRef.current = null
          handleSync()
        }
      }, 500)
      popupCheckIntervalRef.current = intervalId
    } catch (error: unknown) {
      toast.error(
        getApiErrorDetail(error, 'Failed to fetch authentication token')
      )
    }
  }, [headlessTokenMutation, handleSync])

  const handleDelete = useCallback(
    async (senderEmail: SenderEmail) => {
      const confirmed = await confirm(
        `Are you sure you want to delete "${senderEmail.name || senderEmail.email}"?`,
        'Delete Sender Email',
        'This will permanently remove the sender email. This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) return

      try {
        await deleteMutation.mutateAsync(senderEmail.id)
        toast.success('Sender email deleted successfully')
      } catch (error: unknown) {
        toast.error(getApiErrorDetail(error, 'Failed to delete sender email'))
      }
    },
    [deleteMutation]
  )

  const getRowActions = useCallback(
    (senderEmail: SenderEmail) => [
      {
        label: 'Edit',
        icon: PiPencilSimple,
        onClick: () => setEditingSenderEmail(senderEmail),
      },
      {
        label: 'Delete',
        icon: PiTrash,
        onClick: () => handleDelete(senderEmail),
        variant: 'destructive' as const,
        disabled: deleteMutation.isPending,
      },
    ],
    [handleDelete, deleteMutation.isPending]
  )

  const tableHeader = (
    <div
      data-table-toolbar
      className="flex flex-row flex-wrap justify-between items-center gap-3 px-4 py-3"
    >
      <p className="text-sm text-gray-500 min-w-0 flex-1">{DESCRIPTION_TEXT}</p>
      <div className="flex items-center gap-3 shrink-0">
        <FbButton
          variant="secondary"
          size="sm"
          onClick={handleSync}
          disabled={syncMutation.isPending}
          loading={syncMutation.isPending}
          className="!text-gray-800 !border-gray-300 hover:!bg-gray-100 hover:!text-gray-900 hover:!border-gray-300 hover:!shadow-none"
        >
          <PiArrowsClockwise size={16} />
          Sync email accounts
        </FbButton>
        <FbButton
          variant="primary"
          size="sm"
          onClick={handleConnectEmail}
          disabled={headlessTokenMutation.isPending}
          loading={headlessTokenMutation.isPending}
          className="!bg-brand-bright !text-white hover:!bg-brand-active"
        >
          <PiPlus size={16} />
          Connect Email
        </FbButton>
      </div>
    </div>
  )

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <SenderEmailsTable
          data={senderEmails?.results ?? []}
          isLoading={isLoading || isFetching}
          tableKey="settings-sender-emails-table"
          tableHeader={tableHeader}
          getRowActions={getRowActions}
        />
      </div>
      {editingSenderEmail && (
        <EditSenderEmailModal
          isOpen
          onClose={() => setEditingSenderEmail(null)}
          senderEmail={editingSenderEmail}
        />
      )}
    </TableLinkStyleProvider>
  )
}
